import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { initials, eur } from "@/lib/format";
import { PageHeader, StatCard } from "@/components/StatCard";
import Seo from "@/components/Seo";
import ProjectsTab from "@/pages/coach/collab/ProjectsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, Handshake, Users, Inbox, Send, Search, Check, X, Link2, Plus,
  MessageCircle, GitBranch, Archive, Rocket, RefreshCw, GraduationCap, Package, Trash2, Settings2, Gem,
  BarChart3, Sparkles,
} from "lucide-react";

const DURATIONS = [15, 30, 45, 60, 90];
const STATUS_CLS = {
  draft: "bg-secondary text-muted-foreground",
  pending_accept: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700",
  archived: "bg-secondary text-muted-foreground/70",
};
const STATUS_KEY = {
  draft: "col.st_draft",
  pending_accept: "col.st_pending",
  published: "col.st_published",
  archived: "col.st_archived",
};
const KIND_KEY = { one_on_one: "col.kind_one_on_one", training: "col.kind_training", product: "col.kind_product" };
const BILLING_KEY = { one_time: "col.bill_one_time", monthly: "col.bill_monthly", yearly: "col.bill_yearly" };

function statusMeta(t, status) {
  const key = STATUS_KEY[status] || STATUS_KEY.draft;
  return { label: t(key), cls: STATUS_CLS[status] || STATUS_CLS.draft };
}
const kindLabel = (t, k) => (KIND_KEY[k] ? t(KIND_KEY[k]) : k);
const billingLabel = (t, b) => (BILLING_KEY[b] ? t(BILLING_KEY[b]) : b);

function CoachAvatar({ coach, size = "h-10 w-10" }) {
  return (
    <Avatar className={size}>
      {coach?.photo && <AvatarImage src={coach.photo} alt={coach?.name} />}
      <AvatarFallback>{initials(coach?.name)}</AvatarFallback>
    </Avatar>
  );
}

export default function Collab() {
  const { user } = useAuth();
  const t = useT();
  const [summary, setSummary] = useState(null);
  const [savingOpen, setSavingOpen] = useState(false);
  const [chat, setChat] = useState(null);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/coach/collab/summary"); setSummary(data); }
    catch { setSummary({}); }
  }, []);
  useEffect(() => { if (user?.role === "coach") loadSummary(); }, [user, loadSummary]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const toggleOpen = async (v) => {
    setSummary((s) => ({ ...s, open_for_collab: v }));
    setSavingOpen(true);
    try { await api.put("/coach/collab/availability", { open_for_collab: v }); }
    catch (err) { toast.error(apiError(err)); setSummary((s) => ({ ...s, open_for_collab: !v })); }
    finally { setSavingOpen(false); }
  };

  const s = summary || {};

  return (
    <div data-testid="coach-collab-page">
      <Seo title={t("col.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="collab-back">
        <ChevronLeft className="h-4 w-4" /> {t("col.back")}
      </Link>
      <PageHeader eyebrow={t("col.eyebrow")} title={t("col.title")} subtitle={t("col.subtitle")} />

      <div className="mb-5 flex items-center gap-3 rounded-2xl border border-primary/30 bg-primary/5 p-4" data-testid="collab-open-toggle">
        <Handshake className="h-5 w-5 text-primary" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{t("col.open_title")}</p>
          <p className="text-xs text-muted-foreground">{t("col.open_desc")}</p>
        </div>
        <Switch checked={!!s.open_for_collab} disabled={savingOpen} onCheckedChange={toggleOpen} data-testid="collab-open-switch" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label={t("col.stat_connected")} value={s.connected_coaches ?? 0} icon={Users} testId="collab-stat-connected" />
        <StatCard label={t("col.stat_in")} value={s.pending_in ?? 0} icon={Inbox} testId="collab-stat-in" />
        <StatCard label={t("col.stat_out")} value={s.pending_out ?? 0} icon={Send} testId="collab-stat-out" />
        <StatCard label={t("col.stat_offerings")} value={s.collab_offerings ?? 0} icon={Link2} testId="collab-stat-offerings" />
      </div>

      <Tabs defaultValue="connected" className="mt-6">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="connected" data-testid="collab-tab-connected">{t("col.tab_connected")}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="collab-tab-requests">{t("col.tab_requests")}</TabsTrigger>
          <TabsTrigger value="find" data-testid="collab-tab-find">{t("col.tab_find")}</TabsTrigger>
          <TabsTrigger value="offerings" data-testid="collab-tab-offerings">{t("col.tab_offerings")}</TabsTrigger>
          <TabsTrigger value="circles" data-testid="collab-tab-circles">{t("col.tab_circles")}</TabsTrigger>
          <TabsTrigger value="projects" data-testid="collab-tab-projects">{t("col.tab_projects")}</TabsTrigger>
          <TabsTrigger value="chats" data-testid="collab-tab-chats">{t("col.tab_chats")}</TabsTrigger>
          <TabsTrigger value="stats" data-testid="collab-tab-stats">{t("col.tab_stats")}</TabsTrigger>
          <TabsTrigger value="ai" data-testid="collab-tab-ai">{t("col.tab_ai")}</TabsTrigger>
        </TabsList>
        <TabsContent value="connected"><ConnectedTab meId={user?.id} onOpenChat={setChat} /></TabsContent>
        <TabsContent value="requests"><RequestsTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="find"><FindTab onChanged={loadSummary} /></TabsContent>
        <TabsContent value="offerings"><OfferingsTab meId={user?.id} onChanged={loadSummary} /></TabsContent>
        <TabsContent value="circles"><CirclesTab meId={user?.id} onChanged={loadSummary} /></TabsContent>
        <TabsContent value="projects"><ProjectsTab /></TabsContent>
        <TabsContent value="chats"><ChatsTab onOpenChat={setChat} /></TabsContent>
        <TabsContent value="stats"><Empty icon={BarChart3} title={t("col.stats_title")} text={t("col.stats_body")} /></TabsContent>
        <TabsContent value="ai"><Empty icon={Sparkles} title={t("col.ai_title")} text={t("col.ai_body")} /></TabsContent>
      </Tabs>

      {chat && <ChatThread chatId={chat.id} title={chat.title} meId={user?.id} onClose={() => setChat(null)} />}
    </div>
  );
}

/* ───────────────────────── Connected ───────────────────────── */

function ConnectedTab({ meId, onOpenChat }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [picker, setPicker] = useState(null); // {coach, clients}
  const [clientQuery, setClientQuery] = useState("");

  useEffect(() => {
    api.get("/coach/collab/connected").then(({ data }) => setRows(data?.connected || [])).catch(() => setRows([]));
  }, []);

  const startDm = async (coach) => {
    setBusy(coach.id);
    try { const { data } = await api.post("/coach/collab/chats/coach-only", { other_coach_id: coach.id }); onOpenChat({ id: data.id, title: coach.name }); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const openSharedPicker = async (coach) => {
    setBusy(coach.id);
    try {
      const { data } = await api.get(`/permissions/eligible-clients/${coach.id}`);
      if (!data || data.length === 0) { toast.message(t("col.no_shared_clients_title"), { description: t("col.no_shared_clients_desc") }); return; }
      setClientQuery(""); setPicker({ coach, clients: data });
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  const createShared = async (client) => {
    try {
      const { data } = await api.post("/collaboration/shared-chat", { coach_a_id: meId, coach_b_id: picker.coach.id, client_id: client.id });
      setPicker(null); onOpenChat({ id: data.id, title: `${picker.coach.name} · ${client.name}` });
    } catch (err) { toast.error(apiError(err)); }
  };

  if (rows === null) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>;
  if (rows.length === 0) return <Empty icon={Users} title={t("col.conn_empty_title")} text={t("col.conn_empty_text")} />;

  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.collaboration_id} data-testid={`collab-connected-${r.coach.id}`} className="rounded-xl border border-border bg-card p-3">
          <div className="flex items-center gap-3">
            <CoachAvatar coach={r.coach} />
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-foreground">{r.coach.name}</p>
              {r.coach.headline && <p className="truncate text-xs text-muted-foreground">{r.coach.headline}</p>}
            </div>
            <div className="shrink-0 text-right text-xs text-muted-foreground">
              <p>{t("col.circles_n", { n: r.shared_circles })}</p>
              <p>{t("col.offers_n", { n: r.shared_offers })}</p>
            </div>
          </div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" className="flex-1 gap-1.5 rounded-full" disabled={busy === r.coach.id} onClick={() => startDm(r.coach)} data-testid={`collab-dm-${r.coach.id}`}>
              <MessageCircle className="h-4 w-4" /> {t("col.message")}
            </Button>
            <Button size="sm" className="flex-1 gap-1.5 rounded-full" disabled={busy === r.coach.id} onClick={() => openSharedPicker(r.coach)} data-testid={`collab-shared-${r.coach.id}`}>
              <GitBranch className="h-4 w-4" /> {t("col.shared_chat")}
            </Button>
          </div>
        </div>
      ))}

      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent data-testid="collab-client-picker">
          <DialogHeader>
            <DialogTitle>{t("col.pick_client_title")}</DialogTitle>
            <DialogDescription>{t("col.pick_client_desc")}</DialogDescription>
          </DialogHeader>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={clientQuery} onChange={(e) => setClientQuery(e.target.value)} placeholder={t("col.search_client_ph")} className="pl-9" data-testid="collab-client-search" />
          </div>
          <div className="max-h-[320px] space-y-1 overflow-y-auto">
            {(picker?.clients || []).filter((c) => !clientQuery || c.name?.toLowerCase().includes(clientQuery.toLowerCase())).map((c) => (
              <button key={c.id} onClick={() => createShared(c)} data-testid={`collab-pick-client-${c.id}`} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-2.5 text-left hover:bg-accent">
                <CoachAvatar coach={c} size="h-9 w-9" />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{c.name}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────────────────── Requests ───────────────────────── */

function RequestsTab({ onChanged }) {
  const t = useT();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/coach/collab/requests"); setData(d); }
    catch { setData({ incoming: [], outgoing: [], history: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (req, accept) => {
    setBusy(req.id);
    try {
      await api.post("/collaboration/respond", { request_id: req.id, accept });
      toast.success(accept ? t("col.req_accepted") : t("col.req_declined"));
      await load(); onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  if (data === null) return <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>;
  const { incoming = [], outgoing = [] } = data;
  if (incoming.length === 0 && outgoing.length === 0) return <Empty icon={Inbox} title={t("col.req_empty_title")} text={t("col.req_empty_text")} />;

  return (
    <div className="space-y-6">
      <div>
        <p className="mb-2 text-sm font-semibold text-foreground">{t("col.incoming_n", { n: incoming.length })}</p>
        {incoming.length === 0 ? <p className="text-sm italic text-muted-foreground">{t("col.no_incoming")}</p> : (
          <div className="space-y-2">
            {incoming.map((r) => (
              <div key={r.id} data-testid={`collab-incoming-${r.id}`} className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-center gap-3">
                  <CoachAvatar coach={r.other_coach} size="h-9 w-9" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-foreground">{r.other_coach?.name}</p>
                    {(r.topic || r.message) && <p className="truncate text-xs text-muted-foreground">{r.topic || r.message}</p>}
                  </div>
                </div>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" className="gap-1.5 rounded-full" disabled={busy === r.id} onClick={() => respond(r, true)} data-testid={`collab-accept-${r.id}`}>
                    <Check className="h-4 w-4" /> {t("col.accept")}
                  </Button>
                  <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === r.id} onClick={() => respond(r, false)} data-testid={`collab-decline-${r.id}`}>
                    <X className="h-4 w-4" /> {t("col.decline")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="mb-2 text-sm font-semibold text-foreground">{t("col.outgoing_n", { n: outgoing.length })}</p>
        {outgoing.length === 0 ? <p className="text-sm italic text-muted-foreground">{t("col.no_outgoing")}</p> : (
          <div className="space-y-2">
            {outgoing.map((r) => (
              <div key={r.id} data-testid={`collab-outgoing-${r.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
                <CoachAvatar coach={r.other_coach} size="h-9 w-9" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{r.other_coach?.name}</p>
                  {(r.topic || r.message) && <p className="truncate text-xs text-muted-foreground">{r.topic || r.message}</p>}
                </div>
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">{t("col.pending")}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Find ───────────────────────── */

function FindTab({ onChanged }) {
  const t = useT();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const search = useCallback(async (query) => {
    setLoading(true);
    try { const { data } = await api.get(`/coach/collab/search?q=${encodeURIComponent(query)}`); setRows(data?.coaches || []); }
    catch { setRows([]); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { const h = setTimeout(() => search(q), 300); return () => clearTimeout(h); }, [q, search]);

  const sendRequest = async () => {
    setSending(true);
    try {
      await api.post("/collaboration/request", { other_coach_id: target.id, topic: topic.trim() || undefined, message: message.trim() || undefined });
      toast.success(t("col.request_sent", { name: target.name }));
      setTarget(null); setTopic(""); setMessage("");
      await search(q); onChanged?.();
    } catch (err) { toast.error(apiError(err, t("col.request_failed"))); }
    finally { setSending(false); }
  };

  return (
    <div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("col.search_coach_ph")} className="pl-9" data-testid="collab-search-input" />
      </div>
      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <Empty icon={Search} title={t("col.no_coaches_title")} text={t("col.no_coaches_text")} />
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <div key={c.id} data-testid={`collab-coach-${c.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
              <CoachAvatar coach={c} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-semibold text-foreground">{c.name}</p>
                {c.headline && <p className="truncate text-xs text-muted-foreground">{c.headline}</p>}
              </div>
              <Button size="sm" className="gap-1.5 rounded-full" onClick={() => setTarget(c)} data-testid={`collab-request-${c.id}`}>
                <Send className="h-4 w-4" /> {t("col.request")}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="collab-request-dialog">
          <DialogHeader><DialogTitle>{t("col.request_to", { name: target?.name })}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.topic_label")}</label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("col.topic_ph")} data-testid="collab-request-topic" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.message_label")}</label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t("col.message_ph")} data-testid="collab-request-message" />
            </div>
          </div>
          <DialogFooter>
            <Button className="gap-1.5 rounded-full" onClick={sendRequest} disabled={sending} data-testid="collab-request-send">
              <Send className="h-4 w-4" /> {sending ? t("col.sending") : t("col.send_request")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────────────────── Gemeinsame Angebote ───────────────────────── */

function OfferingsTab({ meId }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/collab-offerings/mine"); setRows(data?.offerings || []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (o, action) => {
    setBusy(o.id);
    try {
      await api.post(`/coach/collab-offerings/${o.id}/${action}`);
      toast.success({ accept: t("col.off_accepted"), decline: t("col.off_declined"), publish: t("col.off_published"), archive: t("col.off_archived") }[action]);
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button className="gap-1.5 rounded-full" onClick={() => setCreating(true)} data-testid="collab-offering-new">
          <Plus className="h-4 w-4" /> {t("col.new_offering")}
        </Button>
      </div>

      {rows === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <Empty icon={Link2} title={t("col.off_empty_title")} text={t("col.off_empty_text")} />
      ) : (
        <div className="space-y-3">
          {rows.map((o) => {
            const st = statusMeta(t, o.status);
            return (
              <div key={o.id} data-testid={`collab-offering-${o.id}`} className="rounded-2xl border border-border bg-card p-4">
                <button onClick={() => setDetail(o)} className="w-full text-left" data-testid={`collab-offering-open-${o.id}`}>
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 font-display text-lg text-foreground">{o.title}</p>
                    <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex -space-x-2">
                      {(o.coaches || []).slice(0, 5).map((c) => <CoachAvatar key={c.id} coach={c} size="h-7 w-7 border-2 border-card" />)}
                    </div>
                    <p className="truncate text-xs text-muted-foreground">{(o.coaches || []).map((c) => c.name).join(" · ")}</p>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-display text-base font-semibold text-primary">{eur(o.total_price_cents)}</span>
                    <span className="text-xs text-muted-foreground">{t("col.confirmed_of", { confirmed: o.confirmed_count, total: (o.coach_ids || []).length })}{o.bookings_count ? t("col.bookings_suffix", { n: o.bookings_count }) : ""}</span>
                  </div>
                  {o.needs_my_confirmation && <p className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">{t("col.needs_confirm")}</p>}
                  {o.fully_booked && <p className="mt-2 ml-2 inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-700">{t("col.fully_booked")}</p>}
                </button>
                <div className="mt-3 flex flex-wrap gap-2">
                  {o.needs_my_confirmation && (
                    <>
                      <Button size="sm" className="gap-1.5 rounded-full" disabled={busy === o.id} onClick={() => act(o, "accept")} data-testid={`collab-offering-accept-${o.id}`}><Check className="h-4 w-4" /> {t("col.accept")}</Button>
                      <Button size="sm" variant="outline" className="gap-1.5 rounded-full" disabled={busy === o.id} onClick={() => act(o, "decline")} data-testid={`collab-offering-decline-${o.id}`}><X className="h-4 w-4" /> {t("col.decline")}</Button>
                    </>
                  )}
                  {o.is_creator && o.all_confirmed && o.status === "draft" && (
                    <Button size="sm" className="gap-1.5 rounded-full" disabled={busy === o.id} onClick={() => act(o, "publish")} data-testid={`collab-offering-publish-${o.id}`}><Rocket className="h-4 w-4" /> {t("col.publish")}</Button>
                  )}
                  {o.is_creator && o.status !== "archived" && (
                    <Button size="sm" variant="ghost" className="gap-1.5 rounded-full text-muted-foreground" disabled={busy === o.id} onClick={() => act(o, "archive")} data-testid={`collab-offering-archive-${o.id}`}><Archive className="h-4 w-4" /> {t("col.archive")}</Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {creating && <CreateCollabOffering meId={meId} onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
      {detail && <OfferingDetail offering={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function evenSplit(ids) {
  const n = ids.length;
  const base = Math.floor(100 / n);
  const out = {};
  ids.forEach((id) => { out[id] = base; });
  out[ids[0]] += 100 - base * n;
  return out;
}

let _uid = 0;
function makeBlock(kind, coachIds) {
  return {
    uid: `b${Date.now()}_${_uid++}`,
    kind,
    title: "",
    description: "",
    priceEur: kind === "product" ? "0" : "100",
    executing: [...coachIds],
    method: "assigned",
    assign: evenSplit(coachIds),
    days: kind === "training" ? [{ date: "", start: "", end: "" }] : [],
    maxp: "",
    duration: 60,
    product: null,
  };
}
function reconcileBlock(b, coachIds) {
  let exec = b.executing.filter((id) => coachIds.includes(id));
  if (exec.length === 0) exec = [...coachIds];
  const relevant = b.method === "assigned" ? exec : coachIds;
  return { ...b, executing: exec, assign: evenSplit(relevant) };
}

function BlockEditor({ index, block, coachIds, coachName, catalog, onChange, onRemove }) {
  const t = useT();
  const set = (patch) => onChange({ ...block, ...patch });
  const relevant = block.method === "assigned" ? block.executing : coachIds;
  const products = (catalog?.coaches || []).flatMap((c) => (c.items || []).map((it) => ({ ...it, owner: c.name })));

  const toggleExec = (id) => {
    let exec = block.executing.includes(id) ? block.executing.filter((x) => x !== id) : [...block.executing, id];
    if (exec.length === 0) exec = block.executing;
    set({ executing: exec, assign: evenSplit(block.method === "assigned" ? exec : coachIds) });
  };
  const setMethod = (m) => set({ method: m, assign: evenSplit(m === "assigned" ? block.executing : coachIds) });
  const setDay = (i, patch) => set({ days: block.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const addDay = () => set({ days: [...block.days, { date: "", start: "", end: "" }] });
  const delDay = (i) => set({ days: block.days.filter((_, j) => j !== i) });

  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid={`collab-offering-block-${index}`}>
      <div className="mb-2 flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          {block.kind === "training" ? <GraduationCap className="h-3.5 w-3.5" /> : block.kind === "product" ? <Package className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
          {kindLabel(t, block.kind)}
        </span>
        <Button size="icon" variant="ghost" className="h-7 w-7 rounded-full text-rose-600" onClick={onRemove} data-testid={`collab-offering-block-del-${index}`}><Trash2 className="h-4 w-4" /></Button>
      </div>

      <Input value={block.title} onChange={(e) => set({ title: e.target.value })} placeholder={t("col.block_title_ph")} className="mb-2" data-testid={`collab-offering-block-title-${index}`} />

      {block.kind === "product" ? (
        <select value={block.product ? `${block.product.coach_id}::${block.product.kind}::${block.product.ref_id}` : ""}
          onChange={(e) => {
            const it = products.find((p) => `${p.coach_id}::${p.kind}::${p.ref_id}` === e.target.value);
            set(it ? { product: { coach_id: it.coach_id, kind: it.kind, ref_id: it.ref_id }, title: block.title || it.title, priceEur: (it.price_cents / 100).toFixed(2) } : { product: null });
          }}
          className="mb-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`collab-offering-block-product-${index}`}>
          <option value="">{t("col.pick_product_ph")}</option>
          {products.map((p) => (
            <option key={`${p.coach_id}::${p.kind}::${p.ref_id}`} value={`${p.coach_id}::${p.kind}::${p.ref_id}`}>{p.owner}: {p.title} ({eur(p.price_cents)})</option>
          ))}
        </select>
      ) : (
        <div className="mb-2 flex gap-3">
          {block.kind === "one_on_one" && (
            <div className="flex-1">
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.duration_min")}</label>
              <div className="flex flex-wrap gap-1.5">
                {DURATIONS.map((d) => (
                  <button key={d} type="button" onClick={() => set({ duration: d })} data-testid={`collab-offering-block-dur-${index}-${d}`} className={`rounded-full border px-2.5 py-1 text-xs ${block.duration === d ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{d}</button>
                ))}
              </div>
            </div>
          )}
          <div className="w-28">
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.price_eur")}</label>
            <Input type="text" inputMode="decimal" value={block.priceEur} onChange={(e) => set({ priceEur: e.target.value })} data-testid={`collab-offering-block-price-${index}`} />
          </div>
        </div>
      )}

      {block.kind === "training" && (
        <div className="mb-2 space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.days")}</label>
            <Button size="sm" variant="outline" className="h-7 gap-1 rounded-full text-xs" onClick={addDay} data-testid={`collab-offering-block-addday-${index}`}><Plus className="h-3 w-3" /> {t("col.day")}</Button>
          </div>
          {block.days.map((d, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Input type="date" value={d.date} onChange={(e) => setDay(i, { date: e.target.value })} className="flex-1" data-testid={`collab-offering-block-day-date-${index}-${i}`} />
              <Input type="time" value={d.start} onChange={(e) => setDay(i, { start: e.target.value })} className="w-24" data-testid={`collab-offering-block-day-start-${index}-${i}`} />
              <Input type="time" value={d.end} onChange={(e) => setDay(i, { end: e.target.value })} className="w-24" data-testid={`collab-offering-block-day-end-${index}-${i}`} />
              {block.days.length > 1 && <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 rounded-full text-rose-600" onClick={() => delDay(i)}><X className="h-4 w-4" /></Button>}
            </div>
          ))}
          <div>
            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.max_participants")}</label>
            <Input type="number" min="0" value={block.maxp} onChange={(e) => set({ maxp: e.target.value })} className="w-32" data-testid={`collab-offering-block-maxp-${index}`} />
          </div>
        </div>
      )}

      <div className="mb-2">
        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.executing_coaches")}</label>
        <div className="flex flex-wrap gap-1.5">
          {coachIds.map((id) => (
            <button key={id} type="button" onClick={() => toggleExec(id)} className={`rounded-full border px-2.5 py-1 text-xs ${block.executing.includes(id) ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{coachName(id)}</button>
          ))}
        </div>
      </div>

      <div>
        <div className="mb-1 flex items-center justify-between">
          <label className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{t("col.split_pct")}</label>
          <div className="flex gap-1">
            <button type="button" onClick={() => setMethod("assigned")} className={`rounded-full border px-2 py-0.5 text-[11px] ${block.method === "assigned" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{t("col.split_executing")}</button>
            <button type="button" onClick={() => setMethod("percentage")} className={`rounded-full border px-2 py-0.5 text-[11px] ${block.method === "percentage" ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{t("col.split_all")}</button>
          </div>
        </div>
        <div className="space-y-1">
          {relevant.map((id) => (
            <div key={id} className="flex items-center gap-2">
              <span className="min-w-0 flex-1 truncate text-xs text-foreground">{coachName(id)}</span>
              <Input type="number" min="0" max="100" value={block.assign[id] ?? 0} onChange={(e) => set({ assign: { ...block.assign, [id]: e.target.value } })} className="h-8 w-20" data-testid={`collab-offering-block-split-${index}-${id}`} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CreateCollabOffering({ meId, onClose, onSaved }) {
  const t = useT();
  const [connected, setConnected] = useState(null);
  const [picked, setPicked] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [catalog, setCatalog] = useState({ coaches: [] });
  const [overrideOn, setOverrideOn] = useState(false);
  const [overrideEur, setOverrideEur] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/coach/collab/connected").then(({ data }) => setConnected((data?.connected || []).map((r) => r.coach))).catch(() => setConnected([]));
  }, []);

  const coachIds = [meId, ...picked];
  const coachIdsKey = coachIds.join(",");
  useEffect(() => {
    setBlocks((prev) => prev.map((b) => reconcileBlock(b, [meId, ...picked])));
    if (coachIds.length) api.get(`/coach/collab-offerings-catalog?coach_ids=${encodeURIComponent(coachIds.join(","))}`).then(({ data }) => setCatalog(data || { coaches: [] })).catch(() => {});
  }, [coachIdsKey]);

  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const addBlock = (kind) => setBlocks((b) => [...b, makeBlock(kind, coachIds)]);
  const updateBlock = (i, nb) => setBlocks((b) => b.map((x, j) => (j === i ? nb : x)));
  const removeBlock = (i) => setBlocks((b) => b.filter((_, j) => j !== i));
  const coachName = (id) => (id === meId ? t("col.you") : (connected || []).find((c) => c.id === id)?.name || t("col.coach_fallback"));

  const blockCents = (b) => Math.round(parseFloat(String(b.priceEur).replace(",", ".")) * 100) || 0;
  const sumCents = blocks.reduce((a, b) => a + blockCents(b), 0);

  const save = async () => {
    if (title.trim().length < 3) { toast.error(t("col.err_title_min")); return; }
    if (picked.length === 0) { toast.error(t("col.err_pick_coach")); return; }
    if (blocks.length === 0) { toast.error(t("col.err_add_block")); return; }
    const payloadBlocks = [];
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      if (!b.title.trim()) { toast.error(t("col.err_block_title", { n: i + 1 })); return; }
      const relevant = b.method === "assigned" ? b.executing : coachIds;
      const sum = relevant.reduce((a, id) => a + (parseInt(b.assign[id]) || 0), 0);
      if (sum !== 100) { toast.error(t("col.err_block_split", { n: i + 1, sum })); return; }
      const pb = {
        kind: b.kind,
        title: b.title.trim(),
        description: b.description.trim(),
        price_cents: blockCents(b),
        executing_coach_ids: b.executing,
        assignment_method: b.method,
        assignments: relevant.map((id) => ({ coach_id: id, percentage: parseInt(b.assign[id]) || 0 })),
      };
      if (b.kind === "training") {
        const days = b.days.filter((d) => d.date && d.start && d.end);
        if (days.length === 0) { toast.error(t("col.err_block_day", { n: i + 1 })); return; }
        pb.days = days.map((d) => ({ date: d.date, start_time: d.start, end_time: d.end }));
        pb.max_participants = parseInt(b.maxp) || 0;
      } else if (b.kind === "one_on_one") {
        pb.duration_min = b.duration;
      } else if (b.kind === "product") {
        if (!b.product) { toast.error(t("col.err_block_product", { n: i + 1 })); return; }
        pb.product_ref = b.product;
      }
      payloadBlocks.push(pb);
    }
    if (sumCents <= 0) { toast.error(t("col.err_total_zero")); return; }
    const body = { title: title.trim(), description: description.trim(), coach_ids: coachIds, blocks: payloadBlocks };
    if (overrideOn) {
      const oc = Math.round(parseFloat(String(overrideEur).replace(",", ".")) * 100) || 0;
      if (oc > sumCents) { toast.error(t("col.err_override_high")); return; }
      body.total_price_cents = oc; body.total_price_override = true;
    }
    setSaving(true);
    try {
      await api.post("/coach/collab-offerings", body);
      toast.success(t("col.offering_created"));
      onSaved();
    } catch (err) { toast.error(apiError(err, t("col.create_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="collab-offering-dialog">
        <DialogHeader>
          <DialogTitle>{t("col.new_offering")}</DialogTitle>
          <DialogDescription>{t("col.dialog_new_offering_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_title")}</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("col.offering_title_ph")} data-testid="collab-offering-title" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_description")}</label>
            <Textarea className="min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="collab-offering-desc" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.coaches_connected")}</label>
            {connected === null ? <Skeleton className="h-12 rounded-lg" /> : connected.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("col.no_connected_coaches")}</p>
            ) : (
              <div className="space-y-1.5">
                {connected.map((c) => (
                  <label key={c.id} data-testid={`collab-offering-coach-${c.id}`} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 ${picked.includes(c.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" checked={picked.includes(c.id)} onChange={() => togglePick(c.id)} className="h-4 w-4 accent-primary" />
                    <CoachAvatar coach={c} size="h-8 w-8" />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.blocks")}</label>
              <div className="flex gap-1.5">
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" onClick={() => addBlock("one_on_one")} data-testid="collab-offering-add-one_on_one"><Plus className="h-3 w-3" /> 1:1</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" onClick={() => addBlock("training")} data-testid="collab-offering-add-training"><Plus className="h-3 w-3" /> {t("col.kind_training")}</Button>
                <Button size="sm" variant="outline" className="h-8 gap-1 rounded-full text-xs" onClick={() => addBlock("product")} data-testid="collab-offering-add-product"><Plus className="h-3 w-3" /> {t("col.kind_product")}</Button>
              </div>
            </div>
            {blocks.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">{t("col.add_block_hint")}</p>
            ) : (
              <div className="space-y-2">
                {blocks.map((b, i) => (
                  <BlockEditor key={b.uid} index={i} block={b} coachIds={coachIds} coachName={coachName} catalog={catalog} onChange={(nb) => updateBlock(i, nb)} onRemove={() => removeBlock(i)} />
                ))}
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("col.sum_blocks")}</span>
              <span className="font-display text-base font-semibold text-foreground">{eur(sumCents)}</span>
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm text-foreground">
              <Switch checked={overrideOn} onCheckedChange={setOverrideOn} data-testid="collab-offering-override" />
              {t("col.override_price")}
            </label>
            {overrideOn && (
              <Input type="text" inputMode="decimal" value={overrideEur} onChange={(e) => setOverrideEur(e.target.value)} placeholder={t("col.package_price_ph")} className="mt-2 w-40" data-testid="collab-offering-override-price" />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="collab-offering-save">{saving ? t("common.saving") : t("col.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OfferingDetail({ offering, onClose }) {
  const t = useT();
  const [data, setData] = useState(offering);
  useEffect(() => {
    api.get(`/coach/collab-offerings/${offering.id}`).then(({ data: d }) => setData(d)).catch(() => {});
  }, [offering.id]);
  const st = statusMeta(t, data.status);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="collab-offering-detail">
        <DialogHeader>
          <DialogTitle>{data.title}</DialogTitle>
          <DialogDescription>{data.description || t("col.offering_fallback")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${st.cls}`}>{st.label}</span>
            <span className="font-display text-lg font-semibold text-primary">{eur(data.total_price_cents)}</span>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.coaches")}</p>
            <div className="flex flex-wrap gap-2">
              {(data.coaches || []).map((c) => (
                <span key={c.id} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2 py-1 text-xs text-foreground">
                  <CoachAvatar coach={c} size="h-5 w-5" /> {c.name}{data.confirmations?.[c.id] ? " ✓" : ""}
                </span>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.blocks")}</p>
            <div className="space-y-2">
              {(data.blocks || []).map((b) => (
                <div key={b.id} className="rounded-xl border border-border bg-card p-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-foreground">{b.title}</p>
                    <span className="text-sm text-primary">{eur(b.price_cents)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">{kindLabel(t, b.kind)}{b.duration_min ? ` · ${b.duration_min} min` : ""}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Gemeinsame Kreise ───────────────────────── */

function CirclesTab({ meId }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/collab/circles"); setRows(data?.circles || []); }
    catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirm = async (c) => {
    setBusy(c.id);
    try { await api.patch(`/coach/collab/circles/${c.id}/confirm`); toast.success(t("col.circ_confirmed")); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button className="gap-1.5 rounded-full" onClick={() => setCreating(true)} data-testid="collab-circle-new">
          <Plus className="h-4 w-4" /> {t("col.new_circle")}
        </Button>
      </div>
      {rows === null ? (
        <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <Empty icon={Gem} title={t("col.circ_empty_title")} text={t("col.circ_empty_text")} />
      ) : (
        <div className="space-y-3">
          {rows.map((c) => {
            const published = c.status === "published";
            return (
              <div key={c.id} data-testid={`collab-circle-${c.id}`} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-2">
                  <p className="min-w-0 flex-1 font-display text-lg text-foreground">{c.name}</p>
                  <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{published ? t("col.st_published") : t("col.circ_pending")}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex -space-x-2">{(c.coaches || []).slice(0, 5).map((x) => <CoachAvatar key={x.id} coach={x} size="h-7 w-7 border-2 border-card" />)}</div>
                  <p className="truncate text-xs text-muted-foreground">{(c.coaches || []).map((x) => x.name).join(" · ")}</p>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-display text-base font-semibold text-primary">{eur(c.price_cents)} · {billingLabel(t, c.billing_period)}</span>
                  <span className="text-xs text-muted-foreground">{t("col.confirmed_of", { confirmed: c.confirmed_count, total: (c.co_coach_ids || []).length })}</span>
                </div>
                {c.needs_my_confirmation && (
                  <Button size="sm" className="mt-3 gap-1.5 rounded-full" disabled={busy === c.id} onClick={() => confirm(c)} data-testid={`collab-circle-confirm-${c.id}`}><Check className="h-4 w-4" /> {t("col.confirm")}</Button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {creating && <CreateCollabCircle meId={meId} onClose={() => setCreating(false)} onSaved={async () => { setCreating(false); await load(); }} />}
    </div>
  );
}

function CreateCollabCircle({ meId, onClose, onSaved }) {
  const t = useT();
  const [connected, setConnected] = useState(null);
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [priceEur, setPriceEur] = useState("49");
  const [billing, setBilling] = useState("monthly");
  const [seatLimit, setSeatLimit] = useState("");
  const [split, setSplit] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/coach/collab/connected").then(({ data }) => setConnected((data?.connected || []).map((r) => r.coach))).catch(() => setConnected([]));
  }, []);
  const coachIds = [meId, ...picked];
  useEffect(() => { setSplit(evenSplit([meId, ...picked])); }, [meId, picked]);
  const togglePick = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const coachName = (id) => (id === meId ? t("col.you") : (connected || []).find((c) => c.id === id)?.name || t("col.coach_fallback"));

  const save = async () => {
    if (name.trim().length < 3) { toast.error(t("col.err_name_min")); return; }
    if (picked.length === 0) { toast.error(t("col.err_pick_coach")); return; }
    const cents = Math.round(parseFloat(String(priceEur).replace(",", ".")) * 100) || 0;
    const sum = coachIds.reduce((a, id) => a + (parseInt(split[id]) || 0), 0);
    if (sum !== 100) { toast.error(t("col.err_split_100", { sum })); return; }
    setSaving(true);
    try {
      await api.post("/coach/collab/circles", {
        name: name.trim(),
        description: description.trim(),
        price_cents: cents,
        billing_period: billing,
        seat_limit: seatLimit ? parseInt(seatLimit) : null,
        co_coach_ids: coachIds,
        splits: Object.fromEntries(coachIds.map((id) => [id, parseInt(split[id]) || 0])),
      });
      toast.success(t("col.circle_created"));
      onSaved();
    } catch (err) { toast.error(apiError(err, t("col.create_failed"))); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="collab-circle-dialog">
        <DialogHeader>
          <DialogTitle>{t("col.new_circle")}</DialogTitle>
          <DialogDescription>{t("col.dialog_new_circle_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_name")}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("col.circle_name_ph")} data-testid="collab-circle-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_description")}</label>
            <Textarea className="min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="collab-circle-desc" />
          </div>
          <div className="flex gap-3">
            <div className="w-32">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.price_eur")}</label>
              <Input type="text" inputMode="decimal" value={priceEur} onChange={(e) => setPriceEur(e.target.value)} data-testid="collab-circle-price" />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.billing")}</label>
              <div className="flex gap-1.5">
                {Object.entries(BILLING_KEY).map(([v, key]) => (
                  <button key={v} type="button" onClick={() => setBilling(v)} data-testid={`collab-circle-billing-${v}`} className={`rounded-full border px-3 py-1.5 text-sm ${billing === v ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground"}`}>{t(key)}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="w-40">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.seats")}</label>
            <Input type="number" min="1" value={seatLimit} onChange={(e) => setSeatLimit(e.target.value)} data-testid="collab-circle-seats" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.co_coaches_connected")}</label>
            {connected === null ? <Skeleton className="h-12 rounded-lg" /> : connected.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("col.no_connected_coaches")}</p>
            ) : (
              <div className="space-y-1.5">
                {connected.map((c) => (
                  <label key={c.id} data-testid={`collab-circle-coach-${c.id}`} className={`flex cursor-pointer items-center gap-3 rounded-lg border p-2.5 ${picked.includes(c.id) ? "border-primary bg-primary/5" : "border-border"}`}>
                    <input type="checkbox" checked={picked.includes(c.id)} onChange={() => togglePick(c.id)} className="h-4 w-4 accent-primary" />
                    <CoachAvatar coach={c} size="h-8 w-8" />
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{c.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {picked.length > 0 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.rev_split")}</label>
              <div className="space-y-1.5">
                {coachIds.map((id) => (
                  <div key={id} className="flex items-center gap-3">
                    <span className="min-w-0 flex-1 truncate text-sm text-foreground">{coachName(id)}</span>
                    <Input type="number" min="0" max="100" value={split[id] ?? 0} onChange={(e) => setSplit((s) => ({ ...s, [id]: e.target.value }))} className="w-24" data-testid={`collab-circle-split-${id}`} />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">{t("col.sum_100")}</p>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="collab-circle-save">{saving ? t("common.saving") : t("col.create")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ───────────────────────── Chats ───────────────────────── */

function ChatsTab({ onOpenChat }) {
  const t = useT();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [offerOpen, setOfferOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/coach/collab/chats?filter=${filter}`); setRows(data?.chats || []); }
    catch { setRows([]); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const FILTERS = [["all", "col.filter_all"], ["coach-only", "col.filter_coaches"], ["with-client", "col.filter_with_client"]];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {FILTERS.map(([v, key]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`collab-chats-filter-${v}`} className={`rounded-full border px-3 py-1.5 text-sm ${filter === v ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>{t(key)}</button>
          ))}
        </div>
        <Button size="sm" variant="outline" className="gap-1.5 rounded-full" onClick={() => setOfferOpen(true)} data-testid="collab-chatoffer-open">
          <Settings2 className="h-4 w-4" /> {t("col.chat_offer")}
        </Button>
      </div>
      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
      ) : rows.length === 0 ? (
        <Empty icon={MessageCircle} title={t("col.chats_empty_title")} text={t("col.chats_empty_text")} />
      ) : (
        <div className="space-y-2">
          {rows.map((c) => {
            const names = (c.participants || []).map((p) => p?.name).filter(Boolean).join(", ");
            return (
              <button key={c.id} onClick={() => onOpenChat({ id: c.id, title: names || t("col.chat_fallback") })} data-testid={`collab-chat-${c.id}`} className="flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left hover:bg-accent">
                <div className="flex -space-x-2">
                  {(c.participants || []).slice(0, 3).map((p, i) => <CoachAvatar key={i} coach={p} size="h-9 w-9 border-2 border-card" />)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate font-semibold text-foreground">{names || t("col.chat_fallback")}</p>
                    <span className="shrink-0 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">{c.mode === "coach-only" ? t("col.badge_coach") : t("col.badge_client")}</span>
                  </div>
                  {c.last_message?.body && <p className="truncate text-xs text-muted-foreground">{c.last_message.from_name}: {c.last_message.body}</p>}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {offerOpen && <ChatOfferDialog onClose={() => setOfferOpen(false)} />}
    </div>
  );
}

function ChatOfferDialog({ onClose }) {
  const t = useT();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    api.get("/coach/collab/chat-offer/me").then(({ data }) => setForm({
      enabled: !!data.enabled,
      title: data.title || "Collab Chat",
      description: data.description || "",
      priceEur: data.price_cents ? (data.price_cents / 100).toFixed(2) : "0",
      unlimited: !!data.unlimited,
      included_messages: data.included_messages ?? "",
      includes_phone_call: !!data.includes_phone_call,
      phone_call_minutes: data.phone_call_minutes ?? "",
      extras: (data.extras || []).join(", "),
    })).catch(() => setForm({ enabled: false, title: "Collab Chat", description: "", priceEur: "0", unlimited: false, included_messages: "", includes_phone_call: false, phone_call_minutes: "", extras: "" }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/coach/collab/chat-offer", {
        enabled: form.enabled,
        title: form.title.trim() || "Collab Chat",
        description: form.description.trim(),
        price_cents: Math.round(parseFloat(String(form.priceEur).replace(",", ".")) * 100) || 0,
        unlimited: form.unlimited,
        included_messages: form.unlimited || form.included_messages === "" ? null : parseInt(form.included_messages) || 0,
        includes_phone_call: form.includes_phone_call,
        phone_call_minutes: form.includes_phone_call && form.phone_call_minutes !== "" ? parseInt(form.phone_call_minutes) || 0 : null,
        extras: form.extras.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(t("col.chatoffer_saved"));
      onClose();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="collab-chatoffer-dialog">
        <DialogHeader>
          <DialogTitle>{t("col.chatoffer_title")}</DialogTitle>
          <DialogDescription>{t("col.chatoffer_desc")}</DialogDescription>
        </DialogHeader>
        {form === null ? (
          <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm font-medium text-foreground">{t("col.chatoffer_active")}</span>
              <Switch checked={form.enabled} onCheckedChange={(v) => set({ enabled: v })} data-testid="collab-chatoffer-enabled" />
            </label>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_title")}</label>
              <Input value={form.title} onChange={(e) => set({ title: e.target.value })} data-testid="collab-chatoffer-title" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.f_description")}</label>
              <Textarea className="min-h-[60px]" value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="collab-chatoffer-desc" />
            </div>
            <div className="w-32">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.price_eur")}</label>
              <Input type="text" inputMode="decimal" value={form.priceEur} onChange={(e) => set({ priceEur: e.target.value })} data-testid="collab-chatoffer-price" />
            </div>
            <label className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm font-medium text-foreground">{t("col.unlimited_msgs")}</span>
              <Switch checked={form.unlimited} onCheckedChange={(v) => set({ unlimited: v })} data-testid="collab-chatoffer-unlimited" />
            </label>
            {!form.unlimited && (
              <div className="w-40">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.included_msgs")}</label>
                <Input type="number" min="0" value={form.included_messages} onChange={(e) => set({ included_messages: e.target.value })} data-testid="collab-chatoffer-msgs" />
              </div>
            )}
            <label className="flex items-center justify-between rounded-xl border border-border p-3">
              <span className="text-sm font-medium text-foreground">{t("col.phone_included")}</span>
              <Switch checked={form.includes_phone_call} onCheckedChange={(v) => set({ includes_phone_call: v })} data-testid="collab-chatoffer-phone" />
            </label>
            {form.includes_phone_call && (
              <div className="w-40">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.phone_minutes")}</label>
                <Input type="number" min="0" value={form.phone_call_minutes} onChange={(e) => set({ phone_call_minutes: e.target.value })} data-testid="collab-chatoffer-phone-min" />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col.extras")}</label>
              <Input value={form.extras} onChange={(e) => set({ extras: e.target.value })} placeholder={t("col.extras_ph")} data-testid="collab-chatoffer-extras" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving || form === null} data-testid="collab-chatoffer-save">{saving ? t("common.saving") : t("col.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChatThread({ chatId, title, meId, onClose }) {
  const t = useT();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/collaboration/shared-chat/${chatId}/messages`); setMsgs(Array.isArray(data) ? data : []); }
    catch { setMsgs([]); }
  }, [chatId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/collaboration/shared-chat/${chatId}/messages`, { body });
      setMsgs((m) => [...(m || []), data]); setText("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="flex h-[70vh] max-h-[640px] flex-col" data-testid="collab-chat-thread">
        <DialogHeader className="flex-row items-center justify-between">
          <DialogTitle className="truncate">{title || t("col.chat_fallback")}</DialogTitle>
          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={load} data-testid="collab-chat-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </DialogHeader>
        <div className="-mx-2 flex-1 space-y-2 overflow-y-auto px-2">
          {msgs === null ? (
            <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 rounded-xl" />)}</div>
          ) : msgs.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">{t("col.no_messages")}</p>
          ) : (
            msgs.map((m) => {
              const mine = m.from_user_id === meId;
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
                    {!mine && <p className="mb-0.5 text-[11px] font-semibold opacity-70">{m.from_name}</p>}
                    <p className="whitespace-pre-wrap break-words">{m.deleted ? <span className="italic opacity-60">{t("col.msg_deleted")}</span> : m.body}</p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        <div className="flex items-center gap-2 border-t border-border pt-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={t("col.msg_input_ph")} data-testid="collab-chat-input" />
          <Button size="icon" className="shrink-0 rounded-full" onClick={send} disabled={sending} data-testid="collab-chat-send"><Send className="h-4 w-4" /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Empty({ icon: Icon, title, text }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Icon className="h-6 w-6" /></div>
      <p className="mt-4 font-display text-lg text-foreground">{title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
